import React, { FC, useState } from 'react';
import { useMutation } from 'react-query';
import { Upload, Spin, Modal } from 'antd';
import { FileOutlined, UploadOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/lib/upload';
import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ImageResponse, uploadImage } from '~/api/file';
import useFileList, { rcFileToAndtImage } from '~/hooks/useFileList';
import useImageCache from '~/hooks/useImageCache';
import theme from '~/theme';

interface Props extends Omit<UploadProps, 'onChange'> {
  label: string;
  name: string;
  onChange?: (values: ImageResponse | ImageResponse[] | undefined) => void;
  value?: ImageResponse | ImageResponse[];
  multiple?: boolean;
  variant?: 'default' | 'drawer';
  anonymous?: boolean;
  [x: string]: any;
}

const ImageInput: FC<Props> = (props) => {
  const {
    name,
    label,
    onChange,
    value = [],
    maxCount,
    multiple = true,
    variant = 'default',
    accept = 'image/*',
    anonymous,
    ...rest
  } = props;

  const [previewState, setPreviewState] = useState(previewInitialState);

  const fileList = useFileList([value].flat(1));
  const imagePost = useMutation(
    uploadImage({
      anonymous,
    })
  );
  const [, setImageCache] = useImageCache();

  const { t } = useTranslation('kyc');

  const handlers = {
    // Upload
    beforeUpload: async (rcFile: RcFile) => {
      const res = await imagePost.mutateAsync(rcFile);
      setImageCache((prev) => ({
        ...prev,
        [res.id]: {
          isLoading: false,
          error: null,
          antdImage: rcFileToAndtImage(rcFile, res.id),
          apiImage: res,
        },
      }));
      onChange?.(multiple ? (value as ImageResponse[]).concat([res]) : res);
      return false;
    },
    onRemove: (file: UploadFile<any>) => {
      onChange?.(
        multiple ? (value as ImageResponse[]).filter((item) => item.id !== file.uid) : undefined
      );
      return false;
    },
    // Preview
    onPreview: (file: UploadFile<any>) => {
      const imageUrl = file.thumbUrl;
      if (imageUrl) {
        setPreviewState({
          open: true,
          name: file.name,
          imageUrl,
          type: file.type ?? '',
        });
      }
      return false;
    },
    onClose: () => {
      setPreviewState(previewInitialState);
    },
  };

  const uploadButton = (
    <div>
      <UploadOutlined />
      <div>{label}</div>
    </div>
  );

  const isLimitReached = typeof maxCount === 'number' && maxCount === fileList?.length;
  const isNonMultipleLimitReached = multiple === false && fileList?.length === 1;

  return (
    <>
      <StyledUpload
        accept={accept}
        name={name}
        listType="picture-card"
        disabled={imagePost.isLoading}
        fileList={fileList as any}
        beforeUpload={handlers.beforeUpload}
        onRemove={handlers.onRemove}
        onPreview={handlers.onPreview}
        maxCount={maxCount}
        variant={variant}
        isImageUrl={(file: UploadFile) => !!file.type?.startsWith('image/')}
        iconRender={() => <FileOutlined color={theme.primaryColor} />}
        {...rest}
      >
        {imagePost.isLoading ? (
          <Spin />
        ) : isLimitReached || isNonMultipleLimitReached ? null : (
          uploadButton
        )}
      </StyledUpload>
      <Modal
        visible={previewState.open}
        title={previewState.name}
        footer={null}
        onCancel={handlers.onClose}
        centered
      >
        {!previewState.type.includes('image') ? (
          <a
            target="_blank"
            rel="noopener noreferrer"
            type={previewState.type}
            href={previewState.imageUrl}
          >
            {t('Preview File')}
          </a>
        ) : (
          <img alt={previewState.name} style={fullWidth} src={previewState.imageUrl} />
        )}
      </Modal>
    </>
  );
};

const previewInitialState = {
  open: false,
  name: '',
  imageUrl: '',
  type: '',
};

const fullWidth = { width: '100%' };

const StyledUpload = styled(Upload)`
  ${({ variant }: Props) =>
    variant === 'drawer' &&
    css`
      .ant-upload-list-picture-card-container {
        width: calc(50% - 4px);
        height: unset;
        aspect-ratio: 1;
        &:nth-child(2n) {
          margin-right: 0px;
        }
      }
      .ant-upload.ant-upload-select-picture-card {
        width: calc(50% - 4px);
        height: unset;
        aspect-ratio: 1;
        margin-right: 0px;
      }
    `}
`;

export type ImageInputOnChangeEvent = UploadChangeParam<UploadFile<any>> & {
  value: ImageResponse;
};

export default ImageInput;
